/**
 * Race-to-hue mapping for Blood Bowl races.
 * Used by RaceShield and RaceTag components for per-race color theming.
 */

const RACE_HUES: Record<string, number> = {
  orc: 145,
  goblin: 145,
  snotling: 145,
  wood_elf: 145,
  chaos_chosen: 28,
  chaos_renegades: 28,
  khorne: 28,
  shambling_undead: 290,
  necromantic_horror: 290,
  vampire: 290,
  tomb_kings: 290,
  dark_elf: 310,
  halfling: 85,
  human: 250,
  imperial_nobility: 250,
  bretonnian: 250,
  old_world_alliance: 250,
  elven_union: 185,
  high_elf: 185,
  dwarf: 55,
  chaos_dwarf: 55,
  skaven: 155,
  underworld_denizens: 155,
  lizardmen: 110,
  nurgle: 120,
  ogre: 60,
  amazon: 170,
  gnome: 45,
  norse: 220,
  black_orc: 135,
}

const RACE_INITIALS: Record<string, string> = {
  orc: "O",
  goblin: "Go",
  snotling: "Sn",
  wood_elf: "WE",
  chaos_chosen: "CC",
  chaos_renegades: "CR",
  khorne: "Kh",
  shambling_undead: "SU",
  necromantic_horror: "NH",
  vampire: "Va",
  tomb_kings: "TK",
  dark_elf: "DE",
  halfling: "Hf",
  human: "Hu",
  imperial_nobility: "IN",
  bretonnian: "Br",
  old_world_alliance: "OW",
  elven_union: "EU",
  high_elf: "HE",
  dwarf: "Dw",
  chaos_dwarf: "CD",
  skaven: "Sk",
  underworld_denizens: "UD",
  lizardmen: "Li",
  nurgle: "Nu",
  ogre: "Og",
  amazon: "Am",
  gnome: "Gn",
  norse: "No",
  black_orc: "BO",
}

const RACE_DISPLAY_NAMES: Record<string, string> = {
  orc: "Orc",
  goblin: "Goblin",
  snotling: "Snotling",
  wood_elf: "Wood Elf",
  chaos_chosen: "Chaos Chosen",
  chaos_renegades: "Chaos Renegades",
  khorne: "Khorne",
  shambling_undead: "Shambling Undead",
  necromantic_horror: "Necromantic Horror",
  vampire: "Vampire",
  tomb_kings: "Tomb Kings",
  dark_elf: "Dark Elf",
  halfling: "Halfling",
  human: "Human",
  imperial_nobility: "Imperial Nobility",
  bretonnian: "Bretonnian",
  old_world_alliance: "Old World Alliance",
  elven_union: "Elven Union",
  high_elf: "High Elf",
  dwarf: "Dwarf",
  chaos_dwarf: "Chaos Dwarf",
  skaven: "Skaven",
  underworld_denizens: "Underworld Denizens",
  lizardmen: "Lizardmen",
  nurgle: "Nurgle",
  ogre: "Ogre",
  amazon: "Amazon",
  gnome: "Gnome",
  norse: "Norse",
  black_orc: "Black Orc",
}

const DEFAULT_HUE = 55

export function getRaceHue(race: string): number {
  return RACE_HUES[race] ?? DEFAULT_HUE
}

export function getRaceInitial(race: string): string {
  return RACE_INITIALS[race] ?? race.slice(0, 2).toUpperCase()
}

/** Derive 1–2 character initials from a team name, or undefined if blank. */
export function getTeamInitials(
  name: string | undefined | null,
): string | undefined {
  const trimmed = name?.trim()
  if (!trimmed) return undefined
  return trimmed
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("")
}

export function getRaceDisplayName(race: string): string {
  return (
    RACE_DISPLAY_NAMES[race] ??
    race
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ")
  )
}

/** Single resolution point: custom hue wins, fallback to race hue. */
export function getTeamHue(race: string, colorHue?: number | null): number {
  return colorHue ?? getRaceHue(race)
}

/** Hue-based shield color generator. */
export function getShieldColors(hue: number): {
  gradientTop: string
  gradientBottom: string
  textColor: string
} {
  return {
    gradientTop: `oklch(0.30 0.10 ${hue})`,
    gradientBottom: `oklch(0.18 0.07 ${hue})`,
    textColor: `oklch(0.72 0.14 ${hue})`,
  }
}

/** Hue-based tag color generator. */
export function getTagColors(hue: number): {
  background: string
  color: string
  borderColor: string
} {
  return {
    background: `oklch(0.25 0.08 ${hue})`,
    color: `oklch(0.70 0.15 ${hue})`,
    borderColor: `oklch(0.35 0.10 ${hue})`,
  }
}

export function getRaceShieldColors(race: string): {
  gradientTop: string
  gradientBottom: string
  textColor: string
} {
  return getShieldColors(getRaceHue(race))
}

export function getRaceTagColors(race: string): {
  background: string
  color: string
  borderColor: string
} {
  return getTagColors(getRaceHue(race))
}
